import { useState, useEffect, useRef } from 'react';

const PricingSection = () => {
  const [visibleCards, setVisibleCards] = useState<boolean[]>([false, false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const pricingPlans = [
    {
      name: '구인광고',
      description: '효과적인 구인 광고 제작',
      price: '5만원',
      popular: false,
      features: [
        '맞춤형 디자인',
        '빠른 제작 (1-2일)',
        '수정 1회 포함',
        '다양한 플랫폼 최적화',
      ],
    },
    {
      name: '홈페이지',
      description: '전문적인 웹사이트 제작',
      price: '10만원',
      popular: true,
      features: [
        '반응형 디자인',
        '맞춤형 레이아웃',
        'SEO 최적화',
        '빠른 로딩 속도',
        '수정 2회 포함',
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            pricingPlans.forEach((_, index) => {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const newState = [...prev];
                  newState[index] = true;
                  return newState;
                });
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleConsultClick = () => {
    window.open('http://pf.kakao.com/_KkAxon/chat', '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="pricing" ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">합리적인 가격</h2>
          <p className="text-gray-600 text-lg">
            프로젝트 규모에 맞는 최적의 플랜을 선택하세요
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* 구인광고 */}
          <div className={`bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:border-neon-pink transition-all hover:shadow-2xl ${
            visibleCards[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          } transition-all duration-700`}>
            <h3 className="text-2xl font-bold mb-2 hover:text-neon-pink transition-colors">
              구인광고
            </h3>
            <p className="text-gray-600 text-sm mb-6">효과적인 구인 광고 제작</p>
            <div className="mb-8">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-neon-pink">5만원</span>
                <span className="text-gray-500 text-lg">부터</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-neon-pink text-lg"></i>
                <span className="text-gray-700">맞춤형 디자인</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-neon-pink text-lg"></i>
                <span className="text-gray-700">빠른 제작 (1-2일)</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-neon-pink text-lg"></i>
                <span className="text-gray-700">수정 1회 포함</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-neon-pink text-lg"></i>
                <span className="text-gray-700">다양한 플랫폼 최적화</span>
              </li>
            </ul>
            <button
              onClick={handleConsultClick}
              className="w-full py-3 rounded-full font-bold transition-all whitespace-nowrap hover:scale-105 bg-gray-100 text-navy hover:bg-neon-pink hover:text-white"
            >
              신청하기
            </button>
          </div>

          {/* 홈페이지 */}
          <div className={`bg-gradient-to-br from-neon-pink to-pink-600 rounded-3xl p-8 shadow-xl relative overflow-hidden hover:shadow-2xl transition-all ${
            visibleCards[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          } transition-all duration-700`}>
            <div className="bg-white text-neon-pink text-xs font-bold px-3 py-1 rounded-full inline-block mb-4 animate-pulse">
              인기
            </div>
            <h3 className="text-2xl font-bold mb-2 text-white transition-colors">
              홈페이지
            </h3>
            <p className="text-white/90 text-sm mb-6">전문적인 웹사이트 제작</p>
            <div className="mb-8">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-black text-white">10만원</span>
                <span className="text-white/80 text-lg">부터</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-white text-lg"></i>
                <span className="text-white">반응형 디자인</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-white text-lg"></i>
                <span className="text-white">맞춤형 레이아웃</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-white text-lg"></i>
                <span className="text-white">SEO 최적화</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-white text-lg"></i>
                <span className="text-white">빠른 로딩 속도</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <i className="ri-check-line text-white text-lg"></i>
                <span className="text-white">수정 2회 포함</span>
              </li>
            </ul>
            <button
              onClick={handleConsultClick}
              className="w-full py-3 rounded-full font-bold transition-all whitespace-nowrap hover:scale-105 bg-white text-neon-pink hover:bg-white/90"
            >
              신청하기
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
